import { FC, useMemo } from 'react';
import { Grid, Link, Box, Button } from "@mui/material";
import { rankedTableStyles } from "./ranked-table.styles";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MenuIcon from '@mui/icons-material/Menu';
import { GreenArrowUp, RedArrowDown, AvatarMock } from "../../../icons";
import { LeaderboardPositionDto } from "@sweatpals/shared";
import { clsx } from "../../../utils";

interface RankedRowProps {
  rowInfo: LeaderboardPositionDto;
  variant: 'short' | 'full';
  fileBaseUrl: string;
  homePageUrl?: string;
}

export const RankedRow: FC<RankedRowProps> = ({ rowInfo, variant, fileBaseUrl, homePageUrl }) => {
	const communityPageUrl = useMemo(() => {
		const communityName = rowInfo.community?.userName;

		if (!communityName) {
			return '';
		}

		return `${homePageUrl ? `${homePageUrl}/` : ''}${communityName}`;
	}, [rowInfo.community?.userName, homePageUrl]);

	return (
		<Link href={communityPageUrl} target="_blank">
			<Grid
				container
				sx={clsx(rankedTableStyles['rowWrapper'], {
					width: variant === 'full' ? '814px' : '633px',
				})}
			>
				<Grid
					item
					xs={2}
					sx={{
						fontWeight: 700,
						fontFamily: 'Montserrat',
						fontSize: '13px',
						lineHeight: '19px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '0 9px',
					}}
				>
					<Box>{`#${rowInfo.place}`}</Box>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '2px',
							padding: '1px 4px',
							borderRadius: '25px',
							minWidth: '36px',
							backgroundColor:
								rowInfo.placeDiff && rowInfo.placeDiff > 0
									? 'rgba(61, 213, 152, 0.1)'
									: rowInfo.placeDiff && rowInfo.placeDiff < 0
										? 'rgba(251, 83, 83, 0.1)'
										: 'rgba(255, 255, 255, 0.1)',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							{rowInfo.placeDiff && rowInfo.placeDiff > 0
								? <GreenArrowUp width={12} height={12} />
								: rowInfo.placeDiff && rowInfo.placeDiff < 0
									? <RedArrowDown width={12} height={12} />
									: <MenuIcon sx={{ width: '16px', height: '16px' }} />
							}
						</Box>
						<Box>{rowInfo.placeDiff ? (rowInfo.placeDiff > 0 ? `+${rowInfo.placeDiff}` : rowInfo.placeDiff) : 0}</Box>
					</Box>
				</Grid>
				<Grid
					item
					xs={variant === 'full' ? 4 : 5}
					sx={{
						fontWeight: 600,
						fontFamily: 'Montserrat',
						fontSize: '14px',
						lineHeight: '17px',
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						padding: '0 9px',
					}}
				>
					<Box sx={{ width: '38px', height: '38px', borderRadius: '50%' }}>
						{rowInfo?.community?.avatarId ? (
							<img
								src={`${fileBaseUrl}/${rowInfo.community.avatarId}`}
								style={{ objectFit: 'cover', width: '100%', height: '100%', borderRadius: '50%' }}
							/>
						) : (
							<AvatarMock style={{ borderRadius: '50%', width: '100%', height: '100%' }}/>
						)}
					</Box>
					<Box
						sx={{
							textOverflow: 'ellipsis',
							overflow: 'hidden',
							display: '-webkit-box',
							WebkitLineClamp: 2,
							maxWidth: '180px',
							WebkitBoxOrient: 'vertical',
						}}
					>
						{rowInfo?.community?.fullName}
					</Box>
				</Grid>
				<Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
					{rowInfo?.community?.addresses?.length
						? (rowInfo.community.addresses.length <= 2
							&& rowInfo.community.addresses.reduce((acc, address) => acc + address.addressName.length, 0) <= (variant === 'full' ? 28 : 22)
								? rowInfo.community.addresses.map(address => (
									<Box
										key={address.id}
										sx={{
											padding: '2px 8px',
											fontSize: '13px',
											lineHeight: '21px',
											fontWeight: 500,
											fontFamily: 'Montserrat',
											backgroundColor: 'rgba(255, 255, 255, 0.1)',
											borderRadius: '100px',
											whiteSpace: 'nowrap',
										}}
									>
										{address.addressName}
									</Box>
								))
								: <>
									<Box sx={{
										padding: '2px 8px',
										fontSize: '13px',
										lineHeight: '21px',
										fontWeight: 500,
										fontFamily: 'Montserrat',
										backgroundColor: 'rgba(255, 255, 255, 0.1)',
										borderRadius: '100px',
										whiteSpace: 'nowrap',
										textOverflow: 'ellipsis',
										overflow: 'hidden',
									}}>
										{rowInfo.community.addresses[0].addressName}
									</Box>
									{rowInfo.community.addresses.length - 1 > 0 && (
										<Box sx={{
											padding: '2px 8px',
											fontSize: '13px',
											lineHeight: '21px',
											fontWeight: 500,
											fontFamily: 'Montserrat',
											backgroundColor: 'rgba(255, 255, 255, 0.1)',
											borderRadius: '100px',
										}}>
											+{rowInfo.community.addresses.length - 1}
										</Box>
									)}
								</>
						)
						: null}
				</Grid>
				{variant === 'full' ? (
					<Grid
						item
						xs={2}
						sx={{
							fontWeight: 600,
							fontFamily: 'Montserrat',
							fontSize: '13px',
							lineHeight: '21px',
							padding: '0 9px',
						}}
					>
						{rowInfo?.value}
					</Grid>
				) : <Grid item xs={1}/>}
				<Button
					sx={{
						backgroundColor: 'rgba(52, 52, 52, 1)',
						border: '1px solid rgba(255, 255, 255, 0.1)',
						width: '41px',
						height: '37px',
						position: 'absolute',
						right: '19px',
						top: '50%',
						transform: 'translateY(-50%)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						borderRadius: '100px',
						padding: 0,
						minWidth: 'auto',
						'&:hover': {
							backgroundColor: 'rgba(52, 52, 52, 0.5)',
						}
					}}
				>
					<ArrowForwardIosIcon sx={{ width: '16px', height: '16px', fill: 'white' }} />
				</Button>
			</Grid>
		</Link>
	);
}
